import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Folder as BacklogIcon,
  PlayArrow as SprintIcon
} from '@mui/icons-material';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const MoveWorkItemMenu = ({ 
  issue, 
  projectKey,
  onMove,
  onClose 
}) => {
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);

  // Fetch sprints when component mounts
  useEffect(() => {
    const fetchSprints = async () => {
      if (!projectKey) return;
      
      try {
          const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/sprints/project/${projectKey}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            
            }
          }
        );
        
        if (response.data && response.data.data) {
          setSprints(response.data.data);
        } else {
          setSprints([]);
        }
        setLoading(false);
      } catch (err) {
        //console.error('Error fetching sprints:', err);
        setError('Failed to load sprints');
        setLoading(false);
      }
    };

    fetchSprints();
  }, [projectKey]);

  // Filter options based on search query
  useEffect(() => {
    const allOptions = [
      {
        id: 'backlog',
        name: 'Backlog',
        type: 'backlog',
        icon: <BacklogIcon sx={{ fontSize: '16px', color: '#666' }} />
      },
      ...sprints.map(sprint => ({
        id: sprint.id,
        name: sprint.name || `Sprint ${sprint.id}`,
        type: 'sprint',
        status: sprint.status,
        icon: <SprintIcon sx={{ fontSize: '16px', color: sprint.status === 'ACTIVE' ? '#0052CC' : '#666' }} />
      }))
    ];

    if (searchQuery.trim() === '') {
      setFilteredOptions(allOptions);
    } else {
      const filtered = allOptions.filter(option =>
        option.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [sprints, searchQuery]);

  const handleMove = async (destination) => {
    try {
        const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      if (destination.type === 'backlog') {
        // Move to backlog - remove from sprint
        await axios.delete(`${API_URL}/api/sprints/remove-issue/${issue.key}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
      } else {
        // Move to sprint
        await axios.post(`${API_URL}/api/sprints/add-issue`, {
          issueKey: issue.key,
          sprintId: destination.id,
        },{
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          
        });
      }

      if (onMove) {
        onMove(issue, destination);
      }
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      //console.error('Error moving work item:', error);
      // You might want to show an error toast here
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minWidth: '320px',
          maxWidth: '400px',
          minHeight: '200px',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minWidth: '320px',
          maxWidth: '400px',
          padding: '16px'
        }}
      >
        <Alert severity="error" size="small">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minWidth: '320px',
        maxWidth: '400px',
        maxHeight: '400px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box sx={{ padding: '16px 16px 12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Typography variant="subtitle2" fontWeight="600" color="text.primary">
          Move {issue.key} to
        </Typography>
      </Box>

      {/* Search */}
      <Box sx={{ padding: '12px 16px' }}>
        <TextField
          placeholder="Search destinations"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '18px', color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              '& fieldset': {
                borderColor: '#ddd',
              },
              '&:hover fieldset': {
                borderColor: '#0052CC',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#0052CC',
                borderWidth: '2px',
              },
            }
          }}
        />
      </Box>

      {/* Options List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '3px',
            '&:hover': {
              background: '#a8a8a8',
            },
          },
        }}
      >
        {filteredOptions.length === 0 ? (
          <Box sx={{ padding: '20px', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No destinations found
            </Typography>
          </Box>
        ) : (
          filteredOptions.map((option) => (
            <Box
              key={option.id}
              onClick={() => handleMove(option)}
              sx={{
                padding: '12px 16px',
                cursor: 'pointer',
                '&:hover': { 
                  backgroundColor: '#f8f9fa' 
                },
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #f5f5f5'
              }}
            >
              {option.icon}
              <Box sx={{ ml: 2, flex: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'text.primary'
                  }}
                >
                  {option.name}
                </Typography>
                {option.type === 'sprint' && option.status && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      textTransform: 'capitalize'
                    }}
                  >
                    {option.status.toLowerCase()}
                  </Typography>
                )}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default MoveWorkItemMenu;